import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Share, Plus, Smartphone, X } from "lucide-react";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasRecentlyDismissed() {
  const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  return t && (Date.now() - t) < DISMISS_TTL_MS;
}

/**
 * PWA Install Button
 * - Chrome/Edge/Android: hooks the `beforeinstallprompt` event and calls native prompt
 * - iOS Safari: no native prompt — shows a modal with "Add to Home Screen" instructions
 * - Auto-hides when app is already installed (display-mode: standalone)
 * - "Dismiss" hides the button for 7 days
 */
export function PWAInstallButton({ variant = "compact" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari — no beforeinstallprompt, but we can still show a manual hint
    if (isIOS()) setVisible(true);

    const installed = () => setVisible(false);
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  if (!visible) return null;

  const doInstall = async () => {
    if (isIOS() && !deferredPrompt) {
      setIosHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <>
      <div className={variant === "compact" ? "inline-flex items-center gap-1" : "flex items-center gap-2"}>
        <Button
          data-testid="pwa-install-btn"
          onClick={doInstall}
          size={variant === "compact" ? "sm" : "default"}
          className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/20"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          {variant === "compact" ? "Instalar App" : "Instalar TrueLine no dispositivo"}
        </Button>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          className="text-slate-500 hover:text-slate-300 p-1"
          title="Não mostrar por 7 dias"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <Dialog open={iosHelp} onOpenChange={setIosHelp}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-cyan-400" /> Instalar no iPhone / iPad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 text-sm text-slate-300" data-testid="ios-install-help">
            <p>O iOS Safari não permite instalação automática — você precisa fazer 3 passos rapidinhos:</p>
            <ol className="space-y-3">
              <li className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-bold text-xs flex items-center justify-center shrink-0">1</div>
                <div>Toque no botão <b className="text-cyan-300"><Share className="w-3.5 h-3.5 inline mb-0.5" /> Compartilhar</b> na barra inferior do Safari</div>
              </li>
              <li className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-bold text-xs flex items-center justify-center shrink-0">2</div>
                <div>Role pra baixo e toque em <b className="text-cyan-300"><Plus className="w-3.5 h-3.5 inline mb-0.5" /> Adicionar à Tela de Início</b></div>
              </li>
              <li className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-bold text-xs flex items-center justify-center shrink-0">3</div>
                <div>Toque em <b className="text-cyan-300">Adicionar</b> no canto superior direito — pronto, TrueLine vira ícone no seu iPhone</div>
              </li>
            </ol>
            <div className="rounded-md border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-100/70">
              💡 Depois de instalar, o app abre em tela cheia (sem barra de endereço) e fica na tela de início como um app nativo. Funciona também offline pra Work Orders já sincronizadas.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
